/**
 * "Export conversation" — the WHOLE conversation as PDF, Word, plain text or
 * Markdown, from a conversation's ⋯ menu (the sidebar's recent chats, a
 * branch's row, the meta-agent's session history), just above Delete. Any
 * conversation in the list, not only the open one.
 *
 * The formats open in a submenu BESIDE the item — the Library's pattern:
 * antd's own Menu submenu, so hover or click opens it, the arrow keys open
 * and close it, it opens on the inline-end side and mirrors in Hebrew, and
 * it flips rather than run off the screen. The server builds the file (the
 * transcript in one plain layout, each paragraph in its own direction); this
 * asks for it in the person's UI language and time zone, shows progress
 * while it is made, and saves it under the name the server chose.
 */
import { useState } from 'react';
import { Menu, message as antdMessage } from 'antd';
import type { MenuProps } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import { createLogger } from '@/utils/logger';
import { blobErrorCode } from '@/utils/download';
import conversationService, { type ConversationExportFormat } from '../services/conversationService';
import './ConversationExportMenu.css';

const logger = createLogger('ConversationExport');
const FORMATS: ConversationExportFormat[] = ['pdf', 'docx', 'txt', 'md'];
const EXPORT_KEY = 'export';
/** The flyout is its own overlay, so it must sit above whatever hosts the ⋯
 *  menu: the sidebar's menu (10000) and the meta-agent's history popover,
 *  which takes the top layer (2147483646) — under that it was invisible.
 *  antd computes a submenu popup's z-index itself and lets the item's own
 *  `popupStyle` override it. */
const FLYOUT_Z_INDEX = 2147483647;
/** Hover opens the flyout where the pointer can hover; a touch screen has
 *  no hover that stays, so there a tap opens it (antd's own click trigger). */
const SUBMENU_TRIGGER: 'hover' | 'click' =
  typeof window !== 'undefined' && window.matchMedia?.('(hover: none)').matches ? 'click' : 'hover';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** One export, with its progress toast and a translated error. Independent
 *  of the menu that started it: the menu closes at once, the export goes on. */
async function exportWithFeedback(conversationId: string, format: ConversationExportFormat,
                                  { lang, scope, t }: { lang: string; scope: 'chat' | 'meta'; t: Translate }) {
  const hide = antdMessage.loading(t('chat.export.preparing'), 0);
  try {
    await conversationService.exportConversation(conversationId, format, { lang, scope });
  } catch (err) {
    logger.error('Conversation export failed', err);
    // The export's own rate limit answers 429 with no code of its own.
    const status = (err as { response?: { status?: number } })?.response?.status;
    const code = status === 429 ? 'RATE_LIMITED' : await blobErrorCode(err);
    // "Too long for PDF" names the format the person asked for (only PDF and
    // Word have such a limit).
    const named = format === 'pdf' || format === 'docx'
      ? { format: t(`chat.export.formatNames.${format}`) } : {};
    antdMessage.error(code
      ? t(`chat.export.errors.${code}`, { ...named, defaultValue: t('chat.export.failed') })
      : t('chat.export.failed'));
  } finally {
    hide();
  }
}

interface ConversationExportMenuItemsProps {
  conversationId: string;
  /** Which route reads the conversation: the chat's, or the meta-agent's. */
  scope?: 'chat' | 'meta';
  /** Close the host menu once a format is chosen. */
  onDone?: () => void;
}

export function ConversationExportMenuItems({ conversationId, scope = 'chat', onDone }: ConversationExportMenuItemsProps) {
  const { t, language } = useLanguage();
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const items: MenuProps['items'] = [{
    key: EXPORT_KEY,
    icon: (
      <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
        <path d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"
              stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    label: <span data-testid="conversation-export">{t('chat.export.action')}</span>,
    // The flyout is its own overlay: the host menu's row styling, above it.
    popupClassName: 'conversation-export-flyout',
    popupStyle: { zIndex: FLYOUT_Z_INDEX },
    // A click (or a tap, where there is no hover) opens it too — only opens:
    // the hover that precedes a click has opened it already, and a toggle
    // shut it again (on touch, the emulated hover and the tap cancelled out).
    onTitleClick: () => setOpenKeys([EXPORT_KEY]),
    children: FORMATS.map((format) => ({
      key: format,
      label: <span data-testid={`conversation-export-${format}`}>{t(`chat.export.formats.${format}`)}</span>,
    })),
  }];
  return (
    // The host menus live inside a clickable row, and React carries a click
    // in the flyout's portal up through them: stop it here.
    <div className="conversation-menu-item conversation-export-host" onClick={(e) => e.stopPropagation()}>
      <Menu
        mode="vertical"
        selectable={false}
        triggerSubMenuAction={SUBMENU_TRIGGER}
        className="conversation-export-menu"
        items={items}
        openKeys={openKeys}
        onOpenChange={setOpenKeys}
        onClick={({ key }) => {
          if (!FORMATS.includes(key as ConversationExportFormat)) return;
          setOpenKeys([]);
          onDone?.();
          void exportWithFeedback(conversationId, key as ConversationExportFormat, { lang: language, scope, t });
        }}
      />
    </div>
  );
}

export default ConversationExportMenuItems;
